/**
 * An organisation's financial year, for DISPLAY (ESG-4018).
 *
 * Mirrors `FinancialYearResolver` in the backend, which is the one place a period is decided — every
 * extraction run snapshots the period the server resolved. This file only renders previews and labels, so a
 * drift here shows a wrong date on screen rather than filtering the wrong figures; keep the two in step anyway
 * (their tests share cases).
 *
 * Reporting year Y is the financial year that ENDS in calendar year Y: with 1 Apr – 31 Mar, 2026 means
 * 1 Apr 2025 – 31 Mar 2026. An override for that year wins outright.
 */

export const DEFAULT_FINANCIAL_YEAR = Object.freeze({ startDay: 1, startMonth: 1, endDay: 31, endMonth: 12 });

const pad = (n) => String(n).padStart(2, "0");

const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/** The longest a month can be — 29 for February, so a year ending 29 Feb is valid. */
export const maxDayOfMonth = (month) => daysInMonth(2024, month);

/** An ISO date string, the day clamped to the month's length (29 Feb → 28 Feb outside leap years). */
const clamped = (year, month, day) => `${year}-${pad(month)}-${pad(Math.min(day, daysInMonth(year, month)))}`;

/**
 * @param {{startDay, startMonth, endDay, endMonth, overrides?: Array<{year, startDate, endDate}>}|null} config
 * @param {number} year the reporting year
 * @returns {{start: string, end: string, overridden: boolean}} ISO dates, both inclusive
 */
export function periodFor(config, year) {
  const override = (config?.overrides ?? []).find((o) => Number(o?.year) === Number(year));
  if (override?.startDate && override?.endDate) {
    return { start: override.startDate, end: override.endDate, overridden: true };
  }
  const { startDay, startMonth, endDay, endMonth } = { ...DEFAULT_FINANCIAL_YEAR, ...(config ?? {}) };
  const end = clamped(year, endMonth, endDay);
  let start = clamped(year, startMonth, startDay);
  // ISO strings compare correctly as strings.
  if (start > end) {
    start = clamped(year - 1, startMonth, startDay);
  }
  return { start, end, overridden: false };
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2025-04-01" → "1 Apr 2025". Parsed by hand: `new Date("2025-04-01")` is UTC and can render as 31 Mar. */
export function formatPeriodDate(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  return `${d} ${MONTHS_SHORT[m - 1]} ${y}`;
}

/** "1 Apr 2025 – 31 Mar 2026" */
export function formatPeriod(period) {
  if (!period?.start || !period?.end) return "";
  return `${formatPeriodDate(period.start)} – ${formatPeriodDate(period.end)}`;
}
